'use client';
import React from 'react';
import classNames from 'classnames';
import { hasCldImage, TypeBlockTechnologyBlocks, TypeTechnology } from '@fm-nx/contentful-types';
import { useContentfulInspectorMode, useContentfulLiveUpdates } from '@contentful/live-preview/react';
import LinkWrapper from '../link-wrapper/link-wrapper';
import { CldImage } from 'next-cloudinary';

type TechnologyBlocksPropTypes = {
    blockTechnologyBlocks: TypeBlockTechnologyBlocks<'WITHOUT_UNRESOLVABLE_LINKS'>;
    mode?: 'dark' | 'light';
};

type ValidTechnology = TypeTechnology<'WITHOUT_UNRESOLVABLE_LINKS'>;

export const TechnologyBlocks = (props: TechnologyBlocksPropTypes) => {
    const { blockTechnologyBlocks, mode = 'light' } = props;
    const updatedBlock = useContentfulLiveUpdates(blockTechnologyBlocks);
    const inspectorProps = useContentfulInspectorMode({ entryId: updatedBlock.sys.id });
    const { fields } = updatedBlock;

    if (!fields || !fields.technologies) return null;

    const isDark = mode === 'dark';
    const { overline, headerText, paragraph, technologies } = fields;

    const validTechnologies = technologies.filter((tech): tech is ValidTechnology => !!tech && !!tech.fields);

    if (validTechnologies.length === 0) return null;

    return (
        <section className={classNames({ 'bg-gray-900': isDark })}>
            <div className="w-full mx-auto max-w-container">
                <div className="mx-6 py-14 md:mx-0">
                    <div className="md:grid md:grid-cols-14 md:gap-4">
                        <div className="col-span-14 md:col-span-12 md:col-start-2">
                            <div className="max-w-2xl space-y-6">
                                {overline && (
                                    <h2
                                        className={classNames('h5 overline', { 'text-white': isDark })}
                                        {...inspectorProps({ fieldId: 'overline' })}
                                    >
                                        {overline}
                                    </h2>
                                )}
                                {headerText && (
                                    <h3
                                        className={classNames('h-xl', { 'text-white': isDark })}
                                        {...inspectorProps({ fieldId: 'headerText' })}
                                    >
                                        {headerText}
                                    </h3>
                                )}
                                {paragraph && (
                                    <p
                                        className={classNames('p-xl', { 'text-white': isDark })}
                                        {...inspectorProps({ fieldId: 'paragraph' })}
                                    >
                                        {paragraph}
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 gap-10 mt-14 md:grid-cols-3 md:gap-20">
                                {validTechnologies.map((tech) => (
                                    <TechnologyCard key={tech.sys.id} technology={tech} isDark={isDark} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const TechnologyCard = ({ technology, isDark }: { technology: ValidTechnology; isDark: boolean }) => {
    const updatedTech = useContentfulLiveUpdates(technology);
    const inspectorProps = useContentfulInspectorMode({ entryId: updatedTech.sys.id });
    const { fields } = updatedTech;
    const imageCloudinary = hasCldImage(fields.logo?.fields) ? fields.logo?.fields.cloudinaryMedia[0] : null;

    const content = (
        <div className={classNames('space-y-6', { group: !!fields.slug })}>
            {imageCloudinary && (
                <div className="relative flex items-center h-28" {...inspectorProps({ fieldId: 'logo' })}>
                    <CldImage
                        src={imageCloudinary.public_id}
                        height={50}
                        width={200}
                        format="auto"
                        quality="auto"
                        dpr="auto"
                        alt={fields.title || 'Technology logo'}
                        loading="lazy"
                        className="w-auto h-12"
                    />
                </div>
            )}
            {fields.technologyType && (
                <p
                    className={classNames('h5 overline', { 'text-white': isDark })}
                    {...inspectorProps({ fieldId: 'technologyType' })}
                >
                    {fields.technologyType}
                </p>
            )}
            <h4 className={classNames('h-lg', { 'text-white': isDark })} {...inspectorProps({ fieldId: 'title' })}>
                {fields.title}
            </h4>
            {fields.technologySummary && (
                <p
                    className={classNames('p-xl', { 'text-white': isDark })}
                    {...inspectorProps({ fieldId: 'technologySummary' })}
                >
                    {fields.technologySummary}
                </p>
            )}
            {fields.slug && (
                <div
                    className={classNames('inline-block h5 emdash-push', {
                        'text-white': isDark,
                        'emdash-push--dark': isDark,
                    })}
                >
                    Read More <span className="sr-only">about {fields.title}</span>
                </div>
            )}
        </div>
    );

    if (fields.slug) {
        return (
            <LinkWrapper href={`/partners-and-platforms/${fields.slug}`} className="block">
                {content}
            </LinkWrapper>
        );
    }

    return <div>{content}</div>;
};

export default TechnologyBlocks;
